import { Check, Delete } from "@mui/icons-material";
import { Box, IconButton, ListItem, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { actionButton, actionsContainer, editFieldsContainer, formField, itemContent, itemFormContainer, listItem, notesField } from "../../styles/shoppingListStyles";

const ShoppingListItemForm = ({ item, theme, onSave, onCancel, onDelete }) => {
  const { t } = useTranslation();
  const [localItem, setLocalItem] = useState(item);
  const [errors, setErrors] = useState({
    name: "",
    quantity: "",
    unit: "",
  });

  useEffect(() => {
    setLocalItem(item);
  }, [item]);

  const handleChange = (field, value) => {
    setLocalItem((prev) => ({ ...prev, [field]: value }));

    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateItem = () => {
    const newErrors = {};
    let isValid = true;

    if (!localItem.name || !localItem.name.trim()) {
      newErrors.name = t("shoppingList.nameRequired");
      isValid = false;
    }

    if (!localItem.quantity || localItem.quantity <= 0) {
      newErrors.quantity = t("shoppingList.quantityPositive");
      isValid = false;
    }

    if (!localItem.unit || !localItem.unit.trim()) {
      newErrors.unit = t("shoppingList.unitRequired");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSaveWithValidation = () => {
    if (validateItem() && onSave) {
      const result = onSave(localItem);

      // Determine if this is the "add new" form and not update
      const isAddForm = !onDelete;

      if (isAddForm && result !== false) {
        setLocalItem({
          name: "",
          quantity: 1,
          unit: "",
          notes: "",
          purchased: false,
        });
      }
    }
  };

  const handleKeyDown = (e) => {
    // Enter key saves changes
    if (e.key === "Enter" && onSave) {
      e.preventDefault();
      handleSaveWithValidation();
    }
    // Escape key discards changes
    else if (e.key === "Escape" && onCancel) {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <ListItem divider sx={listItem(theme, true)}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={itemContent(true)}>
          <Box sx={itemFormContainer}>
            <Box sx={editFieldsContainer}>
              <TextField
                label={t("name")}
                value={localItem.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
                size="small"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                sx={formField}
                autoFocus
              />
              <TextField
                label={t("quantity")}
                value={localItem.quantity || ""}
                onChange={(e) => handleChange("quantity", parseFloat(e.target.value) || "")}
                size="small"
                fullWidth
                required
                error={!!errors.quantity}
                helperText={errors.quantity}
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                sx={formField}
              />
              <TextField
                label={t("unit")}
                value={localItem.unit || ""}
                onChange={(e) => handleChange("unit", e.target.value)}
                size="small"
                fullWidth
                required
                error={!!errors.unit}
                helperText={errors.unit}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
                sx={formField}
              />
            </Box>

            <TextField
              label={t("notes")}
              value={localItem.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              size="small"
              fullWidth
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              sx={notesField}
            />
          </Box>
        </Box>

        <Box sx={actionsContainer}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleSaveWithValidation();
            }}
            sx={actionButton(theme, "secondary")}
          >
            <Check />
          </IconButton>
          {onDelete && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={actionButton(theme, "error")}
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

export default ShoppingListItemForm;
